/**
 * Copyright © Veeam Software Group GmbH.
 */

import React from 'react';
import { AdvancedFilter, Search, TOOLBAR_ITEM_TYPE, TOOLBAR_VIEW_TYPE } from '@veeam-vspc/components';
import { capitalize } from '@veeam-vspc/core';

import type { AdvancedFilterOptions, AdvancedFilterValue } from '@veeam-vspc/components';

import { ProtectedVirtualMachineRestoreTypes } from 'core/enums';
import backupJobTypeIconSrc from 'images/filters/job-types/backup.png';
import replicationJobTypeIconSrc from 'images/filters/job-types/replication.png';
import cdpReplicationJobTypeIconSrc from 'images/filters/job-types/cdp.png';
import backupCopyJobTypeIconSrc from 'images/filters/job-types/backup-copy.png';
import tapeJobTypeIconSrc from 'images/filters/job-types/backup-to-tape.png';
import singleJobTypeIconSrc from 'images/pages/protected-data/filters/similar-job-type/single-job.png';
import multipleJobTypeIconSrc from 'images/pages/protected-data/filters/similar-job-type/multiple-jobs.png';
import vmWarePlatformTypeIconSrc from 'images/filters/virtual-platform-types/vsphere.png';
import hyperVPlatformTypeIconSrc from 'images/filters/os/windows.svg';
import nutanixPlatformTypeIconSrc from 'images/filters/virtual-platform-types/nutanix.png';
import yesCloudCopyTypeIconSrc from 'images/filters/backup-agent-cloud-copy-types/yes.png';
import noCloudCopyTypeIconSrc from 'images/filters/backup-agent-cloud-copy-types/no.png';
import { useLang } from 'views/providers/LangProvider/hooks';
import { ToolbarWithExport } from 'views/components/ToolbarWithExport';
import { ProtectedVmCloudCopyStatus, ProtectedVmPlatformTypes, ProtectedVmSimilarJobTypes } from '../../../../../../enums';

import type { ToolbarWithExportProps } from 'views/components/ToolbarWithExport';

export interface VirtualInfrastructureToolbarProps extends Omit<ToolbarWithExportProps, 'method' | 'items'> {
    method?: string;
}

export const VirtualInfrastructureToolbar: React.FC<VirtualInfrastructureToolbarProps> = (props) => {
    const { method = 'GetVirtualMachines', ...restProps } = props;
    const lang = useLang();
    const advancedFilterOptions: AdvancedFilterOptions<AdvancedFilterValue> = {
        header: `${lang.FILTER}:`,
        cols: [
            {
                groups: [
                    {
                        title: lang.TYPE,
                        items: [
                            {
                                iconSrc: backupJobTypeIconSrc,
                                label: lang.BACKUP,
                                value: {
                                    id: ProtectedVirtualMachineRestoreTypes.Backup,
                                    name: 'jobTypes',
                                },
                            },
                            {
                                iconSrc: replicationJobTypeIconSrc,
                                label: lang.REPLICATION,
                                value: {
                                    id: ProtectedVirtualMachineRestoreTypes.Replica,
                                    name: 'jobTypes',
                                },
                            },
                            {
                                iconSrc: cdpReplicationJobTypeIconSrc,
                                label: lang.CDP_REPLICATION,
                                value: {
                                    id: ProtectedVirtualMachineRestoreTypes.CdpReplica,
                                    name: 'jobTypes',
                                },
                            },
                            {
                                iconSrc: backupCopyJobTypeIconSrc,
                                label: lang.BACKUP_COPY,
                                value: {
                                    id: ProtectedVirtualMachineRestoreTypes.Copy,
                                    name: 'jobTypes',
                                },
                            },
                            {
                                iconSrc: tapeJobTypeIconSrc,
                                label: lang.BACKUP_TO_TAPE,
                                value: {
                                    id: ProtectedVirtualMachineRestoreTypes.Tape,
                                    name: 'jobTypes',
                                },
                            },
                        ],
                    },
                ],
            },
            {
                groups: [
                    {
                        title: lang.N_OF_JOB_TYPES,
                        items: [
                            {
                                iconSrc: singleJobTypeIconSrc,
                                label: lang.SINGLE,
                                value: {
                                    id: ProtectedVmSimilarJobTypes.Single,
                                    name: 'similarJobTypes',
                                },
                            },
                            {
                                iconSrc: multipleJobTypeIconSrc,
                                label: lang.MULTIPLE,
                                value: {
                                    id: ProtectedVmSimilarJobTypes.Multiple,
                                    name: 'similarJobTypes',
                                },
                            },
                        ],
                    },
                    {
                        title: capitalize(lang.CLOUD_COPY, true),
                        items: [
                            {
                                iconSrc: yesCloudCopyTypeIconSrc,
                                label: lang.YES,
                                value: {
                                    id: ProtectedVmCloudCopyStatus.CloudCopy,
                                    name: 'cloudCopyStatus',
                                },
                            },
                            {
                                iconSrc: noCloudCopyTypeIconSrc,
                                label: lang.NO,
                                value: {
                                    id: ProtectedVmCloudCopyStatus.NoCopy,
                                    name: 'cloudCopyStatus',
                                },
                            },
                        ],
                    },
                ],
            },
            {
                groups: [
                    {
                        title: lang.PLATFORM,
                        items: [
                            {
                                iconSrc: vmWarePlatformTypeIconSrc,
                                label: lang.VSPHERE,
                                value: {
                                    id: ProtectedVmPlatformTypes.VMware,
                                    name: 'platformTypes',
                                },
                            },
                            {
                                iconSrc: hyperVPlatformTypeIconSrc,
                                label: lang.HYPER_V,
                                value: {
                                    id: ProtectedVmPlatformTypes.HyperV,
                                    name: 'platformTypes',
                                },
                            },
                            {
                                iconSrc: nutanixPlatformTypeIconSrc,
                                label: lang.AHV,
                                value: {
                                    id: ProtectedVmPlatformTypes.Nutanix,
                                    name: 'platformTypes',
                                },
                            },
                        ],
                    },
                ],
            },
        ],
    };

    return (
        <ToolbarWithExport
            {...restProps}
            method={method}
            view={TOOLBAR_VIEW_TYPE.spotted}
            items={[
                {
                    type: TOOLBAR_ITEM_TYPE.customControl,
                    render: () => (
                        <Search
                            name='vmName'
                            placeholder={lang.VM}
                            hasSearchButton={true}
                        />
                    ),
                },
                {
                    type: TOOLBAR_ITEM_TYPE.customControl,
                    render: () => (
                        <AdvancedFilter
                            label={lang.FILTER}
                            options={advancedFilterOptions}
                        />
                    ),
                },
            ]}
        />
    );
};
